import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  VideoCameraIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  ClockIcon,
  PlayIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalMeetings: 0,
    totalPhoneCalls: 0,
    totalTranscripts: 0,
    categorizedCalls: 0,
    gradedCalls: 0,
    recentActivity: [],
    systemHealth: 'healthy'
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const response = await fetch('/api/v1/zoomfabric/dashboard');
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        // Mock data for development
        setStats({
          totalMeetings: 847,
          totalPhoneCalls: 523,
          totalTranscripts: 692,
          categorizedCalls: 425,
          gradedCalls: 308,
          recentActivity: [
            { type: 'categorization', message: '15 sales calls categorized automatically (8 meetings, 7 phone)', timestamp: '1 hour ago' },
            { type: 'grading', message: '12 support interactions graded (5 meetings, 7 phone calls)', timestamp: '3 hours ago' },
            { type: 'phone_sync', message: '523 phone call logs synced from Zoom Phone', timestamp: '6 hours ago' },
            { type: 'connection', message: 'Zoom workspace connected (Meetings + Phone enabled)', timestamp: '1 day ago' }
          ],
          systemHealth: 'healthy'
        });
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">ZoomFabric Dashboard</h1>
        <p className="text-lg text-gray-600 mt-2">
          Monitor your Zoom call intelligence and analysis performance
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <VideoCameraIcon className="w-8 h-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.totalMeetings}</p>
              <p className="text-sm text-gray-600">Zoom Meetings</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <PhoneIcon className="w-8 h-8 text-indigo-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.totalPhoneCalls}</p>
              <p className="text-sm text-gray-600">Phone Calls</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <DocumentTextIcon className="w-8 h-8 text-green-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.totalTranscripts}</p>
              <p className="text-sm text-gray-600">Total Transcripts</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <ChartBarIcon className="w-8 h-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.categorizedCalls}</p>
              <p className="text-sm text-gray-600">Categorized</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <AcademicCapIcon className="w-8 h-8 text-orange-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.gradedCalls}</p>
              <p className="text-sm text-gray-600">Graded</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {stats.recentActivity.map((activity, index) => (
            <div key={index} className="p-6 flex items-start">
              <div className="flex-shrink-0">
                {activity.type === 'categorization' && (
                  <div className="p-2 bg-purple-100 rounded-full">
                    <ChartBarIcon className="w-4 h-4 text-purple-600" />
                  </div>
                )}
                {activity.type === 'grading' && (
                  <div className="p-2 bg-orange-100 rounded-full">
                    <AcademicCapIcon className="w-4 h-4 text-orange-600" />
                  </div>
                )}
                {activity.type === 'connection' && (
                  <div className="p-2 bg-blue-100 rounded-full">
                    <VideoCameraIcon className="w-4 h-4 text-blue-600" />
                  </div>
                )}
                {activity.type === 'job' && (
                  <div className="p-2 bg-green-100 rounded-full">
                    <PlayIcon className="w-4 h-4 text-green-600" />
                  </div>
                )}
                {activity.type === 'phone_sync' && (
                  <div className="p-2 bg-indigo-100 rounded-full">
                    <PhoneIcon className="w-4 h-4 text-indigo-600" />
                  </div>
                )}
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm text-gray-900">{activity.message}</p>
                <p className="text-xs text-gray-500 mt-1">{activity.timestamp}</p>
              </div>
            </div>
          ))}
        </div>

        {stats.recentActivity.length === 0 && (
          <div className="p-12 text-center">
            <ClockIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No recent activity</p>
          </div>
        )}
      </div>
    </div>
  );
}